import {useEffect, useState} from 'react';

const createRequestTracker = () => {
  const state = {
    pendingCount: 0,
    listeners: new Set<(count: number) => void>(),
    isSetup: false,
    originalFetch: window.fetch,
    originalXhrSend: XMLHttpRequest.prototype.send,
  };

  const notifyListeners = () => {
    state.listeners.forEach((listener) => listener(state.pendingCount));
  };

  const increment = () => {
    state.pendingCount += 1;
    notifyListeners();
  };

  const decrement = () => {
    state.pendingCount = Math.max(0, state.pendingCount - 1);
    notifyListeners();
  };

  const setupFetchInterception = () => {
    window.fetch = function (...args) {
      increment();

      return state.originalFetch.apply(this, args).finally(() => {
        decrement();
      });
    };
  };

  const setupXhrInterception = () => {
    XMLHttpRequest.prototype.send = function (...args) {
      increment();

      const decrementOnComplete = () => {
        decrement();
      };

      this.addEventListener('loadend', decrementOnComplete, {once: true});
      this.addEventListener('error', decrementOnComplete, {once: true});
      this.addEventListener('abort', decrementOnComplete, {once: true});

      return state.originalXhrSend.apply(this, args);
    };
  };

  const setup = () => {
    if (state.isSetup) {
      return;
    }

    state.isSetup = true;
    setupFetchInterception();
    setupXhrInterception();
  };

  const cleanup = () => {
    if (!state.isSetup) {
      return;
    }

    window.fetch = state.originalFetch;
    XMLHttpRequest.prototype.send = state.originalXhrSend;
    state.isSetup = false;
    state.pendingCount = 0;
    state.listeners.clear();
  };

  const subscribe = (listener: (count: number) => void) => {
    state.listeners.add(listener);
    return () => {
      state.listeners.delete(listener);
    };
  };

  const getCurrentCount = () => state.pendingCount;

  return {
    setup,
    cleanup,
    subscribe,
    getCurrentCount,
  };
};

const requestTracker = createRequestTracker();

export const usePendingRequests = (): number => {
  const [pendingCount, setPendingCount] = useState<number>(() => requestTracker.getCurrentCount());

  useEffect(() => {
    requestTracker.setup();

    const unsubscribe = requestTracker.subscribe((count) => {
      setPendingCount(count);
    });

    return () => {
      unsubscribe();
      requestTracker.cleanup();
    };
  }, []);

  return pendingCount;
};
